import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { _unionTypeDescription } from './_utils'
import type { Identifiable } from './identifiable'
import { Type } from './type'

export type Id<
  TType extends Type = Type,
  TPartialId extends PartialId = PartialId,
  TContext extends Id.Context = Id.Context,
> = Id.UsingSeparator<Id.Separator.BetweenSections, TType, TPartialId, TContext>
export namespace Id {
  export function getTypeChecker<TType extends Type, TContext extends Context>(
    type?: TType | readonly TType[],
    isContext?: TypeChecker<TContext>,
  ): TypeChecker<Id<TType, PartialId, TContext>> {
    return UsingSeparator.getTypeChecker(
      Separator.BetweenSections,
      type,
      isContext,
    )
  }
  export const isId = TypeChecker.Factory.lazy(() => getTypeChecker())
  export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

  export function from<
    TType extends Type,
    TPartialId extends PartialId,
    TContext extends Id.Context,
  >(
    identifiable: Identifiable<TType, TPartialId, TContext>,
  ): Id<TType, TPartialId, TContext> {
    return isId(identifiable)
      ? identifiable
      : UsingAnySeparator.isUsingAnySeparator(identifiable)
        ? (Separator.swap(
            identifiable,
            Separator.from(identifiable),
            Separator.BetweenSections,
          ) satisfies Id as Id<TType, TPartialId, TContext>)
        : Parts.isParts(identifiable)
          ? join([
              identifiable.type,
              identifiable.context,
              identifiable.partialId,
            ])
          : Parts.Tuple.isTuple(identifiable)
            ? join(identifiable)
            : identifiable.id
  }

  /**
   * There are some cases where either a partial ID or full ID could be used.
   * This enum is helpful for distinguishing which is being used in those
   * cases
   */
  export enum Format {
    /** @see {@link Id} */
    Full = 'full',
    /** @see {@link PartialId} */
    Partial = 'partial',
  }
  // Needs to be before namespace is defined
  const FORMATS: ReadonlySet<Format> = new Set(
    Object.values(Format) as Format[],
  )
  export namespace Format {
    export const ALL: readonly Format[] = Array.from(FORMATS)
    export const isFormat = TypeChecker.Factory.oneOf(FORMATS)
    export const assertIsFormat: TypeAsserter<Format> =
      TypeAsserter.Factory.from(isFormat)

    /**
     * Detects the format of the given id
     */
    export function of(id: Id | PartialId): Format {
      // NOTE: isId is a more specific type-checker, so it is better to use
      // that than PartialId.isPartialId
      return isId(id) ? Format.Full : Format.Partial
    }
  }

  /**
   * {@link Id Ids} encode multiple pieces of information into a
   * single string. These separators are characters that separate the various
   * pieces of information encoded in the id.
   */
  export enum Separator {
    BetweenSections = ':',
    WithinSection = '#',
  }
  // Needs to be before namespace is defined
  const SEPARATORS: ReadonlySet<Separator> = new Set(
    Object.values(Separator) as Separator[],
  )
  export namespace Separator {
    export const ALL: readonly Separator[] = Array.from(SEPARATORS)
    export const isSeparator = TypeChecker.Factory.oneOf(SEPARATORS)
    export const assertIsSeparator: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isSeparator)

    /**
     * Replaces the {@link Separator} in the given {@link Id} with a
     * different {@link Separator}. This is useful to encode an id into
     * another id, or to decode an id from another id.
     */
    export function swap<
      TFromSep extends Separator,
      TToSep extends Separator,
      TId extends UsingSeparator<TFromSep>,
    >(
      id: TId,
      fromSeparator: TFromSep,
      toSeparator: TToSep,
    ): TId extends UsingSeparator<
      TFromSep,
      infer TType,
      infer TPartialId,
      infer TContext
    >
      ? UsingSeparator<TToSep, TType, TPartialId, TContext>
      : UsingSeparator<TToSep> {
      return join(split(id, fromSeparator), toSeparator) as Id as ReturnType<
        typeof swap<TFromSep, TToSep, TId>
      >
    }

    /**
     * Determines which {@link Separator} is being used in the given
     * {@link Id} or {@link Identifiable}. If none are found, it defaults to
     * {@link Separator.BetweenSections}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(identifiable: Identifiable): Separator {
      return (
        Separator.ALL.find(separator =>
          UsingSeparator.getTypeChecker(separator)(identifiable),
        ) ?? Separator.BetweenSections
      )
    }
  }

  /**
   * A {@link Id} but allowing for any {@link Separator} between
   * sections rather than the default {@link Separator.BetweenSections}
   */
  export type UsingAnySeparator<
    TType extends Type = Type,
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = UsingSeparator<Separator, TType, TPartialId, TContext>
  export namespace UsingAnySeparator {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTypeChecker<
      TType extends Type,
      TContext extends Context,
    >(
      type: TType | readonly TType[] = Type.ALL as readonly TType[],
      isContext: TypeChecker<TContext> = TypeChecker.Factory.lazy(
        () => Context.isContext,
      ) as TypeChecker<TContext>,
    ): TypeChecker<UsingAnySeparator<TType, PartialId, TContext>> {
      return UsingSeparator.getTypeChecker(Separator.ALL, type, isContext)
    }
    export const isUsingAnySeparator: TypeChecker<UsingAnySeparator> =
      TypeChecker.Factory.lazy(() => getTypeChecker()).usingAlias(
        'Id.UsingAnySeparator',
      )
  }

  /**
   * A {@link Id} but using the given {@link Separator} between sections
   * rather than the default {@link Separator.BetweenSections}
   */
  export type UsingSeparator<
    TSep extends Separator,
    TType extends Type = Type,
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = `${TType}${TSep}${TContext}${TSep}${TPartialId}`
  export namespace UsingSeparator {
    export type On<TIdentifiable extends Identifiable, TSep extends Separator> =
      TIdentifiable extends Identifiable<
        infer TType,
        infer TPartialId,
        infer TContext
      >
        ? UsingSeparator<TSep, TType, TPartialId, TContext>
        : never
    export namespace On {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTypeChecker<TId extends Id, TSep extends Separator>(
        sep: TSep,
        isOriginalId: TypeChecker<TId>,
      ): TypeChecker<On<TId, TSep>> {
        return sep === Separator.BetweenSections
          ? // If we are using the standard separator, then we can use the
            // provided standard ID checker
            (isOriginalId satisfies TypeChecker<TId> as unknown as TypeChecker<
              On<TId, TSep>
            >)
          : // Otherwise...
            TypeChecker.Factory.custom(
              (val: unknown): val is On<TId, TSep> =>
                // We need to make sure we got a string
                typeof val === 'string' &&
                // That is not in the standard ID format already
                !isOriginalId(val) &&
                // But is in the valid ID format when we switch the separator to
                // the standard separator
                isOriginalId(val.replaceAll(sep, Separator.BetweenSections)),
              {
                get typeDescription() {
                  return `Id.UsingSeparator.On<${isOriginalId.typeDescription}, '${sep}'>`
                },
              },
            )
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTypeChecker<
      TSep extends Separator,
      TType extends Type,
      TContext extends Context,
    >(
      separator: TSep | readonly TSep[] = Separator.ALL as readonly TSep[],
      type: TType | readonly TType[] = Type.ALL as readonly TType[],
      isContext: TypeChecker<TContext> = TypeChecker.Factory.lazy(
        () => Context.isContext,
      ) as TypeChecker<TContext>,
    ): TypeChecker<UsingSeparator<TSep, TType, PartialId, TContext>> {
      const separators = (Separator.isSeparator as TypeChecker<TSep>)(separator)
        ? [separator]
        : separator
      const types = (Type.isType as TypeChecker<TType>)(type) ? [type] : type
      const isPartsTuple = Parts.Tuple.getTypeChecker(types, isContext)
      return TypeChecker.Factory.custom(
        (
          val: unknown,
        ): val is UsingSeparator<TSep, TType, PartialId, TContext> =>
          typeof val === 'string' &&
          separators.some(sep => isPartsTuple(val.split(sep))),
        {
          get typeDescription(): string {
            return `Id.UsingSeparator<${[
              _unionTypeDescription(...separators),
              _unionTypeDescription(...types),
              PartialId.isPartialId.typeDescription,
              isContext.typeDescription,
            ].join(', ')}>`
          },
        },
      )
    }
  }

  /**
   * Object representing the information that is encoded in a {@link Id}
   */
  export interface Parts<
    TType extends Type = Type,
    TPartialId extends PartialId = PartialId,
    TContext extends Context = Context,
  > {
    readonly type: TType
    readonly context: TContext
    readonly partialId: TPartialId
  }
  export namespace Parts {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTypeChecker<
      TType extends Type,
      TContext extends Id.Context,
    >(
      type: TType | readonly TType[],
      isContext: TypeChecker<TContext>,
    ): TypeChecker<Parts<TType, PartialId, TContext>> {
      const types = (Type.isType as TypeChecker<TType>)(type) ? [type] : type
      return TypeChecker.Factory.object<Parts<TType, PartialId, TContext>>({
        context: isContext,
        partialId: PartialId.isPartialId,
        type: TypeChecker.Factory.oneOf(types),
      })
    }
    export const isParts = TypeChecker.Factory.lazy(() =>
      getTypeChecker(Type.ALL, Context.isContext),
    ).usingAlias('Id.Parts')

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<
        infer TType,
        infer TPartialId,
        infer TContext
      >
        ? Parts<TType, TPartialId, TContext>
        : never

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from<TIdentifiable extends Identifiable>(
      identifiable: TIdentifiable,
    ): Of<TIdentifiable> {
      const [type, context, partialId] = Tuple.from(identifiable)
      return { context, partialId, type } satisfies Parts as Of<TIdentifiable>
    }

    export type Tuple<
      TType extends Type = Type,
      TPartialId extends PartialId = PartialId,
      TContext extends Context = Context,
    > = readonly [TType, TContext, TPartialId]
    export namespace Tuple {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTypeChecker<
        TType extends Type,
        TContext extends Id.Context,
      >(
        type: TType | readonly TType[],
        isContext: TypeChecker<TContext>,
      ): TypeChecker<Tuple<TType, PartialId, TContext>> {
        const types = (Type.isType as TypeChecker<TType>)(type) ? [type] : type
        const isTypes = TypeChecker.Factory.oneOf(types)
        return TypeChecker.Factory.tuple(
          isTypes,
          isContext,
          PartialId.isPartialId,
        ).usingAlias(
          () =>
            `Id.Parts.Tuple<${[
              isTypes.typeDescription,
              PartialId.isPartialId.typeDescription,
              isContext.typeDescription,
            ].join(', ')}>`,
        )
      }
      export const isTuple = TypeChecker.Factory.lazy(() =>
        getTypeChecker(Type.ALL, Context.isContext),
      ).usingAlias('Id.Parts.Tuple')

      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Of<TIdentifiable extends Identifiable> =
        TIdentifiable extends Identifiable<
          infer TType,
          infer TPartialId,
          infer TContext
        >
          ? Tuple<TType, TPartialId, TContext>
          : never

      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from<TIdentifiable extends Identifiable>(
        identifiable: TIdentifiable,
      ): Of<TIdentifiable> {
        return split(Id.from(identifiable)) satisfies Tuple as Of<TIdentifiable>
      }
    }

    export type OrTuple<
      TType extends Type = Type,
      TPartialId extends PartialId = PartialId,
      TContext extends Context = Context,
    > = Parts<TType, TPartialId, TContext> | Tuple<TType, TPartialId, TContext>
    export namespace OrTuple {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTypeChecker<
        TType extends Type,
        TContext extends Id.Context,
      >(
        type: TType | readonly TType[],
        isContext: TypeChecker<TContext>,
      ): TypeChecker<OrTuple<TType, PartialId, TContext>> {
        return TypeChecker.Factory.union(
          Parts.getTypeChecker(type, isContext),
          Tuple.getTypeChecker(type, isContext),
        )
      }
      export const isOrTuple = TypeChecker.Factory.lazy(() =>
        getTypeChecker(Type.ALL, Context.isContext),
      ).usingAlias('Id.Parts.OrTuple')

      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Of<TIdentifiable extends Identifiable> =
        | Parts.Of<TIdentifiable>
        | Tuple.Of<TIdentifiable>
    }
  }

  export function split<TId extends UsingSeparator<Separator.BetweenSections>>(
    id: TId,
  ): Parts.Tuple.Of<TId>
  export function split<
    TSep extends Separator,
    TId extends UsingSeparator<TSep>,
  >(id: TId, separator: TSep): Parts.Tuple.Of<TId>
  export function split<
    TSep extends Separator,
    TId extends UsingSeparator<TSep>,
  >(id: TId, separator: TSep = Separator.BetweenSections as TSep): Parts.Tuple {
    return id.split(separator) as readonly string[] as Parts.Tuple
  }

  export function join<const TParts extends Parts.OrTuple>(
    parts: TParts,
  ): UsingSeparator.On<TParts, Separator.BetweenSections>
  export function join<
    TSep extends Separator,
    const TParts extends Parts.OrTuple,
  >(parts: TParts, separator: TSep): UsingSeparator.On<TParts, TSep>
  export function join<
    TSep extends Separator.BetweenSections,
    TType extends Type,
    TPartialId extends PartialId,
    TContext extends Id.Context,
  >(
    parts: Parts.OrTuple<TType, TPartialId, TContext>,
  ): UsingSeparator<TSep, TType, TPartialId, TContext>
  export function join<
    TSep extends Separator,
    TType extends Type,
    TPartialId extends PartialId,
    TContext extends Id.Context,
  >(
    parts: Parts.OrTuple<TType, TPartialId, TContext>,
    // eslint-disable-next-line @typescript-eslint/unified-signatures
    separator: TSep,
  ): UsingSeparator<TSep, TType, TPartialId, TContext>
  export function join<
    TSep extends Separator,
    const TParts extends Parts.OrTuple,
  >(
    parts: TParts,
    separator: TSep = Separator.BetweenSections as TSep,
  ): UsingSeparator.On<TParts, TSep> {
    const partsAsTuple: Parts.Tuple = Parts.Tuple.isTuple(parts)
      ? parts
      : [parts.type, parts.context, parts.partialId]
    return partsAsTuple.join(separator) as UsingSeparator.On<TParts, TSep>
  }

  /* --------------------------------- Context -------------------------------- */
  export type Context<TType extends Type = Type> = string
  export namespace Context {
    export const isContext: TypeChecker<Context> = TypeChecker.isString

    export const NONE = '' satisfies Context
    export type None = typeof NONE
    export namespace None {
      export const isNone: TypeChecker<None> = TypeChecker.Factory.exact(NONE)
    }

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<any, any, infer TContext>
        ? TContext
        : never

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from<TIdentifiable extends Identifiable>(
      identifiable: TIdentifiable,
    ): Of<TIdentifiable> {
      return Parts.from(identifiable)
        .context satisfies Context as Of<TIdentifiable>
    }
  }
}
